import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  CircularProgress,
  Button,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import { WellDataTab } from "./wellDataTab";
import { RenderTimesTab } from "./runTimesTab";
import { HeldTimesTab } from "./heldTimesTab";
import { MainSettingsTab } from "./mainSettingsTab";
import OptimizationTab from "./optimizationTab";
import StartStopTab from "./startStopTab";
import TimedIncrementalTab from "./timedIncrementalTab";
import AnalogSetpointsTab from "./analogSetpointsTab";
import { OverwatchTab } from "./overwatchTab";
import {
  getWellConfig,
  getWellsFromAPI,
  wellsData,
} from "../../utils/wellsConfig";
import { PRC_WELL } from "../../utils/PRC_WELL";
import axios from "../../utils/axiosInstance";
import { ProgressDialog } from "./progressPopup";
import { WellSDSPsTab } from "./plungerSDSPs";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

// Sample Data

export const MainPlungerPage = ({ selectedDevice }) => {
  const [selectedMenu, setSelectedMenu] = useState("Well Data");
  const [selectedTab, setSelectedTab] = useState(1);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressSuccess, setProgressSuccess] = useState(false);
  const [progressError, setProgressError] = useState("");
  const [dynamicWellsData, setDynamicWellsData] = useState({});
  const {
    wellData = [],
    snapshot = [],
    runTimes = {},
    heldTimes = [],
    plungerSDSPs = [],
    optimizationSettings = [],
    proportionalValveControlsSettings = [],
    rocConfig = [],
    mainSettings = [],
    startStopSettings = [],
    timedIncrementalSettings = [],
    analogSetpointsSettings = [],
    overwatchSettings = [],
  } = dynamicWellsData[selectedTab] || {};
  const overwatchValue = useMemo(() => {
    const overwatch = optimizationSettings.find(
      (item) => item.label === "Overwatch"
    );
    return overwatch?.value;
  }, [optimizationSettings]);
  const [bottomTab, setBottomTab] = useState(0);
  const [isWellEnabled, setIsWellEnabled] = useState(true);

  const handleMenuClick = (menu) => setSelectedMenu(menu);
  const handleTabChange = (_, newValue) => {
    setLoading(true);
    setSelectedTab(newValue);
  };

  // ----- WELL DATA CONTENT -----
  const renderWellData = () => (
    <WellDataTab
      selectedTab={selectedTab}
      wellData={wellData}
      snapshot={snapshot}
    />
  );

  // ----- RUN TIMES CONTENT -----
  const renderRunTimes = () => (
    <RenderTimesTab
      runTimes={runTimes}
      setLoading={setLoading}
      selectedTab={selectedTab}
      selectedDevice={selectedDevice}
      refreshData={refreshData}
    />
  );

  // ----- HELD TIMES CONTENT -----
  const renderHeldTimes = () => <HeldTimesTab heldTimes={heldTimes} />;

  //  ----- WELL SD SETPOINTS CONTENT -----
  const renderWellSDSPs = () => (
    <WellSDSPsTab
      plungerSDSPs={plungerSDSPs}
      setLoading={setLoading}
      selectedTab={selectedTab}
      selectedDevice={selectedDevice}
      refreshData={refreshData}
    />
  );

  // ----- PLUNGER SETTINGS NAVBAR -----
  const plungerSettingsNavbar = () => (
    <Box>
      <Tabs
        value={bottomTab}
        onChange={(e, newValue) => setBottomTab(newValue)}
        textColor="inherit"
        TabIndicatorProps={{
          style: { backgroundColor: "#1e293b", marginBottom: 2, padding: 2 },
        }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          bgcolor: "#ffffffff",
          borderTop: "1px solid #333",
          borderBottom: "1px solid #333",
          "& .MuiTab-root": {
            color: "#000000ff",
            minWidth: { xs: "auto", sm: "90px", md: "120px" },
            minHeight: { xs: "40px", sm: "45px", md: "48px" },
            padding: { xs: "6px 8px", sm: "8px 12px", md: "12px 16px" },
            fontWeight: "bold",
            fontSize: { xs: "9px", sm: "12px", md: "14px" },
          },
          "&.Mui-selected": {
            color: "#ffffffff",
            fontWeight: 600,
          },
          "& .MuiTabs-scrollButtons": {
            color: "#000000ff",
            "&.Mui-disabled": { opacity: 0.3 },
          },
        }}
      >
        <Tab label="Main Settings" style={{ fontSize: 12 }} />
        <Tab label="Optimization" style={{ fontSize: 12 }} />
        <Tab label="Overwatch" style={{ fontSize: 12 }} />
        <Tab label="Start / Stop" style={{ fontSize: 12 }} />
        <Tab label="Timed Incremental" style={{ fontSize: 12 }} />
        <Tab label="Analog Setpoints" style={{ fontSize: 12 }} />
      </Tabs>

      {/* Content below the secondary tabs */}
      <Box sx={{ bgcolor: "#374151", p: 2, minHeight: 120 }}>
        {bottomTab === 0 && (
          <MainSettingsTab
            mainSettings={mainSettings}
            setLoading={setLoading}
            selectedTab={selectedTab}
            selectedDevice={selectedDevice}
            refreshData={refreshData}
          />
        )}
        {bottomTab === 1 && (
          <OptimizationTab
            optimizationSettings={optimizationSettings}
            proportionalValveControlsSettings={proportionalValveControlsSettings}
            rocConfig={rocConfig}
            setLoading={setLoading}
            selectedTab={selectedTab}
            selectedDevice={selectedDevice}
            refreshData={refreshData}
          />
        )}
        {bottomTab === 2 && (
          <OverwatchTab
            overwatchSettings={overwatchSettings}
            optimizationSettings={optimizationSettings}
            setLoading={setLoading}
            selectedTab={selectedTab}
            selectedDevice={selectedDevice}
            refreshData={refreshData}
          />
        )}
        {bottomTab === 3 && (
          <StartStopTab
            startStopSettings={startStopSettings}
            setLoading={setLoading}
            selectedTab={selectedTab}
            selectedDevice={selectedDevice}
            refreshData={refreshData}
          />
        )}
        {bottomTab === 4 && (
          <TimedIncrementalTab
            timedIncrementalSettings={timedIncrementalSettings}
            setLoading={setLoading}
            selectedTab={selectedTab}
            selectedDevice={selectedDevice}
            refreshData={refreshData}
          />
        )}
        {bottomTab === 5 && (
          <AnalogSetpointsTab
            analogSetpointsSettings={analogSetpointsSettings}
            optimizationSettings={optimizationSettings}
            setLoading={setLoading}
            selectedTab={selectedTab}
            selectedDevice={selectedDevice}
            refreshData={refreshData}
          />
        )}
      </Box>
    </Box>
  );

  const fetchData = async (withLoading = true) => {
    try {
      if (withLoading) {
        setLoading(true);
        setIsWellEnabled(true); // reset to show spinner until we know the state
      }

      const wells = await getWellsFromAPI(selectedDevice?.id);
      if (!wells) throw new Error("No well data received");

      // If it’s a WellPro II device → automatically disabled
      if (wells?.type === "WellPro II" || wells?.type === "WP") {
        setIsWellEnabled(false);
        setDynamicWellsData({});
        return;
      }

      // Map each tab to the correct enable register
      const enableKeys = {
        1: "R42487",
        2: "R42488",
        3: "R42489",
        4: "R42490",
      };
      const enableKey = enableKeys[selectedTab];
      const isEnabled = wells?.response?.[enableKey] === 1;

      if (!isEnabled) {
        setIsWellEnabled(false);
        // setDynamicWellsData({});
        // return;
      }

      // Fetch config + status only if enabled
      const config = await getWellConfig(selectedTab);
      let status = "";
      const PLG1_SD = wells.response["R40797"];
      const PLG2_SD = wells.response["R40798"];
      const PLG3_SD = wells.response["R40799"];
      const PLG4_SD = wells.response["R40800"];
      const PLG_SD = [0, PLG1_SD, PLG2_SD, PLG3_SD, PLG4_SD];
      const getSD = await PRC_WELL.getSDAsync();
      const sdValue = PLG_SD[selectedTab];
      if (sdValue > 0) {
        status = getSD.C5[sdValue];
      } else {
        status = "Normal Operation";
      }
      const mapped = wellsData(wells.response, config, status, overwatchValue);

      setDynamicWellsData(mapped);
      // setIsWellEnabled(true);
      return mapped;
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsWellEnabled(false);
      setDynamicWellsData({});
    } finally {
      setLoading(false);
    }
  };

  // -------- Initial Load & Tab Change -------- //
  useEffect(() => {
    if (selectedDevice?.id && selectedTab) {
      fetchData();
    }
  }, [selectedTab, selectedDevice?.id]);

  // -------- Refresh Button -------- //
  const refreshData = async () => {
    await fetchData();
  };

  // 🧹 Cleanup polling when unmounting or switching tabs
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (optimizationSettings.length > 0) {
      fetchData(false);
    }
  }, [overwatchValue]);

  const handleGetPlungerData = async () => {
    if (!selectedDevice?.id) {
      console.error("❌ No device selected");
      return;
    }
    // Step 1: Open dialog first
    setProgressOpen(true);
    setProgressSuccess(false);
    // Step 2: Give React a chance to render before doing heavy work
    setTimeout(() => {
      getPlungerData();
    }, 100); // 100ms is enough to show the dialog
  };

  const getPlungerData = async () => {
    const unitId = selectedDevice?.id;
    // Step 1: get initial time
    const initialResp = await axios.get(
      `${process.env.REACT_APP_API_GATEAWAY}/iot/plungers/${unitId}`
    );
    let lastTime = Number(initialResp.data.data?.time || 0);
    // Step 2: send G command
    console.log("🟢 Sending G command...");
    await axios.post(
      `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
      { unitId, cmd: "G",UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: G`,},
      {
        headers: {
          id: "anything",
          "Content-Type": "application/json",
        },
      }
    );
    // await new Promise((resolve) => setTimeout(resolve, 10000));
    // Step 3: start polling every second
    let counter = 0;
    pollRef.current = setInterval(async () => {
      try {
        counter++;
        console.log(`⏳ Checking device (${counter}s)...`);
        const secondResp = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/iot/plungers/${unitId}`
        );
        const currentTime = Number(secondResp.data.data?.time || 0);
        console.log("currentTime", currentTime);
        if (currentTime && currentTime !== lastTime) {
          console.log("🔄 Device time changed:", lastTime, "->", currentTime);
          clearInterval(pollRef.current);
          pollRef.current = null;
          setTimeout(() => {
            setProgressSuccess(true);
          }, 5000);
        }

        if (counter > 50) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          console.warn("⏰ Timeout waiting for device update.");
          setProgressError(
            "⏰ Timeout waiting for device response. Please try again."
          );
        }
      } catch (err) {
        console.warn("Polling error:", err.message);
      }
    }, 4000); // poll every 4 second
  };

  const handleSubmitEnableWell = async () => {
    if (!selectedDevice?.id) {
      console.error("❌ No device selected");
      return;
    }
    // Step 1: Open dialog first
    setProgressOpen(true);
    setProgressSuccess(false);
    // Step 2: Give React a chance to render before doing heavy work
    setTimeout(() => {
      enableWell();
    }, 100); // 100ms is enough to show the dialog
  };

  const enableWell = async () => {
    let pollInterval = null;
    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const pollForChange = async ({
      unitId,
      registerList,
      lastTime,
      lastResponse,
      durationMs,
      intervalMs,
      onSuccess,
      onTimeout,
    }) => {
      const start = Date.now();

      pollInterval = setInterval(async () => {
        try {
          const resp = await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
            registerList
          );

          const { time: currentTime, response: currentResponse } =
            resp.data.data || {};

          const timeChanged =
            currentTime && Number(currentTime) !== Number(lastTime);

          let valueChanged = false;
          for (const key in currentResponse) {
            if (currentResponse[key] !== lastResponse[key]) {
              valueChanged = true;
              break;
            }
          }

          console.log("⏳ Polling...", {
            currentTime,
            timeChanged,
            valueChanged,
          });

          if (timeChanged && valueChanged) {
            stopPolling();
            onSuccess({
              currentTime,
              currentResponse,
            });
            console.log("🔄 Device time changed:", lastTime, "->", currentTime);
          }

          if (Date.now() - start >= durationMs) {
            stopPolling();
            onTimeout();
          }
        } catch (err) {
          console.warn("Polling error:", err.message);
        }
      }, intervalMs);
    };
    try {
      let cmd = "A";
      const unitId = selectedDevice?.id;
      const wellConfig = await getWellConfig(selectedTab);
      let registerList = [wellConfig.DISABLE_WELL];
      console.log("register list in body", registerList);
      // Step 1: get initial time
      const initialResp = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
        registerList
      );
      let lastTime = Number(initialResp.data.data?.time || 0);
      let lastResponse = initialResp.data.data?.response || {};
      console.log("Initial time:", lastTime);
      console.log("regList:", lastResponse);
      cmd += "," + (wellConfig.DISABLE_WELL - 40001) + ",1";
      // Step 2: Send A command
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
        { unitId, cmd ,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${cmd}`,},
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Sending command:", cmd);
      console.log("🟡 Polling for 30s...");
      // STEP 3: poll for 30s BEFORE sending P
      pollForChange({
        unitId,
        registerList,
        lastTime,
        lastResponse,
        durationMs: 30000,
        intervalMs: 4000,

        onSuccess: () => {
          console.log("✅ Update done after A command");
          setProgressSuccess(true);
        },

        onTimeout: async () => {
          console.log("⏰ No change after A, sending P command...");

          // === STEP 4: build & send P ===
          let pCmd = cmd.replace("A", "P");
          pCmd = pCmd.replace(/(-?\d+),\s*(?:-?\d+(?:\.\d+)?|NaN)/gi, "$1,1");
          pCmd += `,${wellConfig.STATE - 40001},1`;

          await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
            { unitId, cmd: pCmd,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${pCmd}`,},
            { headers: { id: "anything", "Content-Type": "application/json" } }
          );

          console.log("🟢 P command sent, polling again...");

          // === STEP 5: poll AFTER P ===
          pollForChange({
            unitId,
            registerList,
            lastTime,
            lastResponse,
            durationMs: 170000,
            intervalMs: 4000,

            onSuccess: () => {
              console.log("✅ Update done after P command");
              setProgressSuccess(true);
            },

            onTimeout: () => {
              console.warn("❌ Timeout after P command");
              setProgressError(
                "⏰ Timeout waiting for device response. Please try again."
              );
            },
          });
        },
      });
    } catch (err) {
      console.error("plgSendCommand failed:", err);
      setProgressOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "#1e293b",
        color: "#fff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top Tabs */}
      <Box sx={{ bgcolor: "#111", borderBottom: "1px solid #333" }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          textColor="inherit"
          TabIndicatorProps={{
            style: {
              backgroundColor: "#fff",
              marginBottom: 2,
              padding: 2,
            },
          }}
        >
          <Tab label="Well 1" value={1} sx={{ fontSize: { xs: 11, md: 13 } }} />
          <Tab label="Well 2" value={2} sx={{ fontSize: { xs: 11, md: 13 } }} />
          <Tab label="Well 3" value={3} sx={{ fontSize: { xs: 11, md: 13 } }} />
          <Tab label="Well 4" value={4} sx={{ fontSize: { xs: 11, md: 13 } }} />
        </Tabs>
      </Box>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#ffffffff",
          color: "#000",
          px: 2,
          py: 1,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "bold", fontSize: 14 }}
        >
          {selectedMenu === "Well Data"
            ? `Well ${selectedTab} Data`
            : selectedMenu === "Run Times"
            ? "Historical Run Time Data"
            : selectedMenu === "Held Times"
            ? "Historical Plunger Held Data"
            : `Plunger ${selectedTab} SD Setpoints`}
        </Typography>
        {/* {
          ! isWellEnabled && ( <Typography
          variant="subtitle1"
          sx={{ fontWeight: "bold", fontSize: 14, backgroundColor:"red", color:"white", p:1 }}

        >Well {selectedTab} is disabled </Typography>)
        } */}
        
        <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            sx={{ fontWeight: "bold", borderWidth:2, fontSize: { xs: "12px", sm: "13px", md: "14px" } }}
            onClick={() => handleSubmitEnableWell()}
          >
            Enable Well {selectedTab}
           </Button>
          <IconButton size="small" onClick={refreshData}>
            <RefreshIcon color="secondary" sx={{ fontSize: { xs: "22px", sm: "23px", md: "24px" } }} />
            <Typography
              color="secondary"
              sx={{ fontSize: { xs: "12px", sm: "13px", md: "14px" }, fontWeight: "bold" }}
            >
              Refresh
            </Typography>
          </IconButton>
          <IconButton size="small" onClick={handleGetPlungerData}>
            <SettingsIcon color="secondary" sx={{ fontSize: { xs: "22px", sm: "23px", md: "24px" } }} />
            <Typography
              color="secondary"
              sx={{ fontSize: { xs: "12px", sm: "13px", md: "14px" }, fontWeight: "bold" }}
            >
              Get Data
            </Typography>
          </IconButton>
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(187, 183, 183, 0.12)",
            backdropFilter: "blur(5px)",
            zIndex: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress color="#60a5fa" size={70} />
        </Box>
      // ) : !isWellEnabled ? (
        // <Box
        //   sx={{
        //     width: "100%",
        //     height: "100%",
        //     bgcolor: "rgba(187, 183, 183, 0.12)",
        //     display: "flex",
        //     justifyContent: "space-between",
        //     alignItems: "center",
        //     padding: 4,
        //   }}
        // >
        //   <Typography sx={{ fontSize: 22, color: "white" }}>
        //     Well {selectedTab} is disabled
        //   </Typography>

        //   <Button
        //     variant="contained"
        //     color="primary"
        //     onClick={() => handleSubmitEnableWell()}
        //   >
        //     Enable Well {selectedTab}
        //   </Button>
        // </Box>
      ) : dynamicWellsData[selectedTab] ? (
        <>
          {/* Well Data, Run Times, Held Times and plunger SD setpoints Sidebar */}
          <Box
            sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" } }}
          >
            {/* Sidebar */}
            <Box
              sx={{
                bgcolor: "#374151",
                borderRight: "1px solid #a0a0a0ff",
                display: "flex",
                p: 1,
                width: { xs: "100%", sm: "25%", lg: "15%" },
              }}
            >
              <List
                sx={{
                  display: { xs: "flex", sm: "block" },
                  flexDirection: { xs: "row", sm: "column" },
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                {[
                  "Well Data",
                  "Run Times",
                  "Held Times",
                  "Plunger SD Setpoints",
                ].map((menu) => (
                  <ListItemButton
                    key={menu}
                    onClick={() => handleMenuClick(menu)}
                    sx={{
                      textAlign: "center",
                      bgcolor:
                        selectedMenu === menu ? "#1e293b" : "transparent",
                      color: "#fff",
                      mb: 1,
                      borderRadius: 0.5,
                      "&:hover": { bgcolor: "#1e293b" },
                    }}
                  >
                    <ListItemText
                      primary={menu}
                      primaryTypographyProps={{
                        fontWeight: "bold",
                        fontSize: 13,
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>

            {selectedMenu === "Well Data" && renderWellData()}
            {selectedMenu === "Run Times" && renderRunTimes()}
            {selectedMenu === "Held Times" && renderHeldTimes()}
            {selectedMenu === "Plunger SD Setpoints" && renderWellSDSPs()}
          </Box>

          {/* plunger settings title */}
          <Box
            sx={{
              bgcolor: "#ffffffff",
              color: "#000",
              px: 2,
              py: 1,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "bold", fontSize: 13 }}
            >
              Plunger Settings
            </Typography>
          </Box>
          {plungerSettingsNavbar()}
        </>
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            bgcolor: "rgba(187, 183, 183, 0.12)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: 20, padding: 2 }}>
            No data available
          </Typography>
        </Box>
      )}
      <ProgressDialog
        open={progressOpen}
        isSuccess={progressSuccess}
        errorMessage={progressError}
        onClose={() => {
          setProgressOpen(false);
          setProgressError("");
        }}
        onRefresh={async () => {
          setProgressOpen(false);
          setProgressError("");
          await refreshData();
        }}
        loadingMessage="Check Device Response. Operation in-progress ..."
        successMessage="Successfully updated!"
      />
    </Box>
  );
};
